import React, { createContext, useContext, useCallback, useEffect, useState, ReactNode } from 'react';

interface ReferralData {
  recruiter_id: string;
  referral_source: string;
  original_job_id?: string;
  company_id?: string;
  timestamp: string;
  expires_at: string;
  token?: string; // Secure token for token-based tracking
  token_metadata?: any; // Additional metadata from token validation
}

interface ReferralContextType {
  referralData: ReferralData | null;
  setReferralData: (data: ReferralData | null) => void;
  clearReferralData: () => void;
  hasActiveReferral: boolean;
  updateReferralJobContext: (jobId: string, companyId?: string) => void;
}

const ReferralTrackingContext = createContext<ReferralContextType | null>(null);

const REFERRAL_STORAGE_KEY = 'referral_tracking_data';
const REFERRAL_DURATION_HOURS = 2160; // 90 days expiration (90 * 24 hours)

export function ReferralTrackingProvider({ children }: { children: ReactNode }) {
  const [referralData, setReferralDataState] = useState<ReferralData | null>(null);

  // Load referral data from localStorage on mount
  useEffect(() => {
    const loadReferralData = () => {
      try {
        const stored = localStorage.getItem(REFERRAL_STORAGE_KEY);
        if (stored) {
          const data = JSON.parse(stored) as ReferralData;
          
          // Check if referral has expired
          const now = new Date();
          const expiresAt = new Date(data.expires_at);
          
          if (now < expiresAt) {
            setReferralDataState(data);
          } else {
            // Expired referral data
            localStorage.removeItem(REFERRAL_STORAGE_KEY);
          }
        }
      } catch (error) {
        console.error('Failed to load referral data:', error);
        localStorage.removeItem(REFERRAL_STORAGE_KEY);
      }
    };

    loadReferralData();
  }, []);

  const setReferralData = useCallback((data: ReferralData | null) => {
    if (data) {
      // First-click attribution - don't overwrite existing referral data
      const existingData = localStorage.getItem(REFERRAL_STORAGE_KEY);
      if (existingData) {
        try {
          const existing = JSON.parse(existingData) as ReferralData;
          const expiresAt = new Date(existing.expires_at);
          
          // Only overwrite if existing data has expired
          if (new Date() < expiresAt) {
            setReferralDataState(existing);
            return;
          }
        } catch (error) {
          console.error('Failed to parse existing referral data:', error);
        }
      }
      
      // Store in both state and localStorage
      localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(data));
      setReferralDataState(data);
    } else {
      // Clear referral data
      localStorage.removeItem(REFERRAL_STORAGE_KEY);
      setReferralDataState(null);
    }
  }, []);

  const clearReferralData = useCallback(() => {
    setReferralData(null);
  }, [setReferralData]);

  const updateReferralJobContext = useCallback((jobId: string, companyId?: string) => {
    if (referralData) {
      // Only update if values actually changed
      const newCompanyId = companyId || referralData.company_id;
      if (referralData.original_job_id === jobId && 
          referralData.company_id === newCompanyId) {
        return; // No change needed
      }
      
      const updatedData = {
        ...referralData,
        original_job_id: jobId,
        company_id: newCompanyId
      };
      setReferralData(updatedData);
    }
  }, [referralData, setReferralData]);

  const hasActiveReferral = referralData !== null;

  return (
    <ReferralTrackingContext.Provider
      value={{
        referralData,
        setReferralData,
        clearReferralData,
        hasActiveReferral,
        updateReferralJobContext
      }}
    >
      {children}
    </ReferralTrackingContext.Provider>
  );
}

export function useReferralTracking() {
  const context = useContext(ReferralTrackingContext);
  if (!context) {
    throw new Error('useReferralTracking must be used within a ReferralTrackingProvider');
  }
  return context;
}